import {Dispatch, FC, SetStateAction, useMemo, useState} from 'react';

import {
  Box,
  Chip,
  Collapse,
  Drawer,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Skeleton,
  styled,
  Typography,
  useMediaQuery,
  Stack
} from '@mui/material';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';

import ChevronDown from 'assets/icons/chevronDown';
import ChevronUp from 'assets/icons/chevronUp';
import Close from 'assets/icons/close';
import {CloseIcon} from 'assets/icons/closeIcon';
import {DiscountIcon} from 'assets/icons/discountIcon';
import InfoIcon from 'assets/icons/infoIcon';
import MakroProIcon from 'assets/icons/makro-pro';
import NewSellerIcon from 'assets/icons/newSeller';
import Points from 'assets/icons/points';
import {TagSmIcon} from 'assets/icons/tagSmIcon';
import ThickChevronUp from 'assets/icons/thickChevronUp';
import WarningCircleIcon from 'assets/icons/warningCircleIcon';
import SummaryModalAppHeader from 'components/app-header-container/BasketAppHeader';
import BasketDeliveryFee from 'components/basket-delivery-fee/BasketDeliveryFee';
import i18n from 'config/i18n/i18n';
import theme from 'config/theme';
import useAnalytics from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useBasketCalculations} from 'hooks/useBasketCalculations';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import {BasketSummaryItemProps, LoyaltyEvent, PricesModel} from 'types';
import {checkIfWeightedFresh, checkIfWeightedFreshSelected, formatDate, formatPriceWithCurrency} from 'utils';
import {filter3PItems} from 'utils/helper';
import {StyledPointsChip} from 'views/address-drawer/styles';
import {Footer} from 'views/basket-footer/BasketFooterContainer';
import {
  StyledBasketSummaryWrapper,
  StyledBorder,
  StyledBox,
  StyledOptions,
  StyledRow,
  StyledUlContainer
} from 'views/basket-summary/styles';

export type BasketSummaryModalProps = {
  showBasketSummaryModal: boolean;
  onCheckoutClick: () => Promise<void>;
  isCheckoutDisabled: boolean;
  loading: boolean;
  onClickSelectAllItems: (e: any) => void;
  setShowBasketSummaryModal: Dispatch<SetStateAction<boolean>>;
};

export const StyledInfoButton = styled('button')(({theme}) => ({
  padding: 0,
  margin: 0,
  outline: 'none',
  border: 0,
  width: 24,
  height: 24,
  marginLeft: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: theme.palette.common.mds_global_color_white
}));

export const StyledListContainer = styled(Box)<{isReactNativeView?: boolean; disableBorder?: boolean}>(
  ({isReactNativeView, theme}) => ({
    width: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    padding: isReactNativeView ? '0px' : theme.spacing(1, 0),
    listStyle: 'none'
  })
);

export const StyledLI = styled('li')(() => ({
  // width: '100%',
  listStyle: 'none'
}));

export const BasketSummaryDialog = styled(Drawer)(() => ({
  '& .MuiPaper-root': {
    width: '100%', // Full width
    height: '100%' // Full height
  }
}));

export const BasketSummaryTitle = styled(DialogTitle)<{isReactNativeView?: boolean}>(({isReactNativeView}) => ({
  padding: isReactNativeView ? '16px' : '16px 16px 12px 16px',
  borderBottom: '1px solid #E8E8E8' // Set border bottom style
}));

export const BasketSummaryContent = styled(DialogContent)(() => ({
  maxHeight: '80vh',
  padding: '0px 16px 0px 16px'
}));

export const DialogFooter = styled(Box)(({theme}) => ({
  '&': {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    margin: '15% 0px 0px 0px',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0)
    }
  }
}));

export const StyledListWrapper = styled(Box)(() => ({
  width: '70%',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  '&:last-child': {
    width: '40%',
    justifyContent: 'flex-end'
  }
}));

export const ListWrapper = styled(Box)<{isReactNativeView?: boolean}>(({isReactNativeView}) => ({
  padding: '8px',
  backgroundColor: '#F4F4F4',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: isReactNativeView ? '12px' : '0px'
}));

export const RestrictedProductsInfo = styled(Box)(() => ({
  padding: '8px 12px',
  backgroundColor: '#FFEBE5',
  borderRadius: '8px',
  display: 'flex',
  gap: '8px',
  height: '44px',
  alignItems: 'center',
  marginBottom: '8px'
}));

export const DeliveryFeeList = styled(List)<{isReactNativeView?: boolean}>(({isReactNativeView}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: isReactNativeView ? '0px' : '8px 0px',
  gap: isReactNativeView ? '12px' : '0px'
}));

export const DiscountList = styled(List)<{isReactNativeView?: boolean}>(({isReactNativeView}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: isReactNativeView ? '0px' : '8px 0px',
  gap: isReactNativeView ? '6px' : '0px'
}));
const formatExpiryDate = formatDate('DD MMMM YYYY');

export const BasketSummaryModal: FC<BasketSummaryModalProps> = ({
  showBasketSummaryModal,
  onCheckoutClick,
  isCheckoutDisabled,
  loading,
  onClickSelectAllItems,
  setShowBasketSummaryModal
}) => {
  const {t} = useTranslation();
  const analytics = useAnalytics();
  const isThai = i18n.language === 'th';

  const [openDiscountList, setOpenDiscountList] = useState(false);
  const [openDeliveryFeeList, setOpenDeliveryFeeList] = useState(true);
  const [isPointsBreakdownOpen, setIsPointsBreakdownOpen] = useState(false);

  const basket = useAppSelector((state) => state?.basket?.basket);
  const promotions = useAppSelector((state) => state?.basket?.promotions);
  const expiringPoints = useAppSelector((state) => state?.campaign.expiringPoints);
  const session = useAppSelector((state) => state?.auth?.idToken);
  const {voucherData, defaultMembershipCard} = useAppSelector((state) => state?.app);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));

  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const selectedItems = selectedItemsLocalStorage.get();

  const hasNoSelectedItems = !selectedItems?.length;
  const hasNoItemsInBasket = !basket?.basketItems.length;
  const items: BasketSummaryItemProps[] = [];
  const prices = basket?.prices as PricesModel;
  const loyaltyDetails = basket?.loyaltyResponseDetails;
  const discount = prices?.roundedDiscountAmount;
  const isAuthenticated = !!session;
  const voucherAmount = voucherData?.amount || 0;
  const totalOriginalPrice = hasNoSelectedItems ? 0 : prices?.totalOriginalPrice;
  const sortedPriceBreakdown =
    prices?.priceBreakdown &&
    [...prices.priceBreakdown].sort((a, b) => {
      return a.is3PSeller === b.is3PSeller ? 0 : a.is3PSeller ? 1 : -1;
    });
  const showLoyaltyOptions = isAuthenticated && loyaltyDetails && defaultMembershipCard;
  const showPointsEarned = isAuthenticated && defaultMembershipCard;
  const isWeightedFreshProduct = checkIfWeightedFresh(basket?.basketItems || []);
  const isWeightedFreshItemSelected = checkIfWeightedFreshSelected(basket?.basketItems || [], selectedItems);
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isMobileScreen = isReactNativeView || isMobileView;

  const {priceUnit, defaultShippingFee} = useBasketCalculations();

  const estimatedPointsDetails = promotions?.estimatedPointsDetails ?? [];
  const togglePointsBreakdown = () => {
    setIsPointsBreakdownOpen(!isPointsBreakdownOpen);
    if (!isPointsBreakdownOpen) {
      analytics.trackPageView(LoyaltyEvent.PRO_POINT_ESTIMATE, {});
    }
  };

  const closestNonNegativeExpiringPoint = useMemo(() => {
    if (!expiringPoints) return null;
    let result = null;
    let closestExpiryDate = dayjs().add(1000, 'years'); // just put something so far away in the future to prevent null checking
    const now = dayjs();

    for (let i = 0; i < expiringPoints.length; i++) {
      const expiringPoint = expiringPoints[i];
      if (expiringPoint.points <= 0) {
        // skip <= 0 points
        continue;
      }
      const expiringPointDate = dayjs(expiringPoints[i]?.expiryDate);
      if (expiringPointDate.isBefore(now)) {
        // skip points that have already expired
        // they have points that already expired too here
        continue;
      }
      if (expiringPointDate.isBefore(closestExpiryDate)) {
        // only take the closest positive after today
        result = expiringPoint;
        closestExpiryDate = expiringPointDate;
      }
    }
    return result;
  }, [expiringPoints]);

  items.push({
    hasExtraOption: false,
    labelType: 'subtotal',
    label: t('label.priceBeforeDiscount'),
    price: formatPriceWithCurrency(totalOriginalPrice, basket?.priceUnit || 'THB')
  });

  const get3PComplexPromotions = () => {
    const basketItems = basket?.basketItems || [];
    const selected3PItems = filter3PItems(basketItems).filter((product) => product.productDetails?.isSelected);
    return selected3PItems.flatMap((product) => {
      const sellerName = product.productDetails?.seller;
      const promotions = product.productDetails?.variants[0]?.complexPromotion3P || [];
      return promotions.map((promo) => `${sellerName} - ${promo?.title}`);
    });
  };

  const checkBasketForProhibitedItems = () => {
    const basketItems = basket?.basketItems || [];
    if (!basketItems.length) return;

    return basketItems.some(
      (product) =>
        product.productDetails?.isSelected &&
        (product.productDetails?.isAlcohol || product.productDetails?.isRestrictedPromotion)
    );
  };

  if ((discount || !!get3PComplexPromotions().length) && !hasNoSelectedItems) {
    const complexPromotions3P = get3PComplexPromotions();
    const hasPromotions = !!basket?.advancedPromotions?.length || !!complexPromotions3P.length;

    const renderDiscountListItems = () => {
      const advancedPromotions =
        basket?.advancedPromotions?.map((advancePromotion, index) => ({
          key: `adv-pro-${index}`,
          text: advancePromotion.description
        })) || [];

      const complex3PPromotions =
        complexPromotions3P?.map((complexPromotion3P, index) => ({
          key: `comp3p-pro-${index}`,
          text: complexPromotion3P
        })) || [];

      const allPromotions = [...advancedPromotions, ...complex3PPromotions];
      const promotionMap = new Map();

      allPromotions.forEach((promo) => {
        if (!promotionMap.has(promo.text)) {
          promotionMap.set(promo.text, promo);
        }
      });

      const uniquePromotions = Array.from(promotionMap.values());

      return uniquePromotions.map((promotion) => (
        <DiscountList key={promotion.key} isReactNativeView={isMobileScreen}>
          <DiscountIcon />
          {!isMobileScreen && <>&nbsp;&nbsp;</>}
          <Box display="flex">
            <Typography fontWeight="inherit" fontSize="12px">
              {promotion.text}
            </Typography>
          </Box>
        </DiscountList>
      ));
    };

    items.push({
      hasExtraOption: true,
      label: t('label.discount'),
      labelType: 'discount',
      priceHighlighted: true,
      price: formatPriceWithCurrency(-discount, basket?.priceUnit),
      options: {
        label: t('label.discount'),
        Icon: hasPromotions ? (
          <Box onClick={() => setOpenDiscountList(!openDiscountList)} display="flex" alignItems="center">
            {openDiscountList ? <ThickChevronUp /> : <ChevronDown />}
          </Box>
        ) : (
          <></>
        )
      },
      collapse: (
        <Collapse in={openDiscountList && hasPromotions} timeout="auto" unmountOnExit>
          <ListWrapper isReactNativeView={isMobileScreen}>{renderDiscountListItems()}</ListWrapper>
        </Collapse>
      )
    });
  }

  items.push({
    hasExtraOption: true,
    label: t('deliveryInfo.title'),
    labelType: 'delivery',
    options: {
      label: t('label.fixedFee'),
      Icon:
        !!sortedPriceBreakdown?.length && !hasNoSelectedItems ? (
          <Box onClick={() => setOpenDeliveryFeeList(!openDeliveryFeeList)} display="flex" alignItems="center">
            {openDeliveryFeeList ? <ThickChevronUp /> : <ChevronDown />}
          </Box>
        ) : (
          <></>
        )
    },
    price:
      hasNoSelectedItems || hasNoItemsInBasket ? (
        formatPriceWithCurrency(0, basket?.priceUnit || 'THB')
      ) : (
        <BasketDeliveryFee />
      ),
    collapse: (
      <Collapse
        in={openDeliveryFeeList && !!sortedPriceBreakdown?.length && !hasNoSelectedItems}
        timeout="auto"
        unmountOnExit
      >
        <>
          {!!basket?.prices?.shippingFee && checkBasketForProhibitedItems() && (
            <RestrictedProductsInfo>
              <Box display="flex" alignItems="center">
                <WarningCircleIcon color="red" />
              </Box>
              <Typography fontSize={'13px'} fontWeight={'400'}>
                {t('deliveryInfo.prohibitedItemsInfo')}
              </Typography>
            </RestrictedProductsInfo>
          )}
          <ListWrapper isReactNativeView={isMobileScreen}>
            {sortedPriceBreakdown?.map((pricesDetails) => (
              <DeliveryFeeList isReactNativeView={isMobileScreen}>
                <Box display="flex" alignItems="center" gap={isMobileScreen ? '6px' : '0px'}>
                  <Box display="flex" alignItems="center">
                    <NewSellerIcon />
                  </Box>
                  {!isMobileScreen && <>&nbsp;&nbsp;</>}
                  {pricesDetails.is3PSeller ? (
                    <Typography fontSize="12px">{pricesDetails.shopName}</Typography>
                  ) : (
                    <Box display="flex" alignItems="center">
                      <MakroProIcon viewBox="9 1 74 16" width="75" height="12" />
                    </Box>
                  )}
                </Box>
                <Box display="flex" alignItems="center">
                  {pricesDetails.shippingFee === 0 ||
                    ((pricesDetails.originalShippingFee || 0) > pricesDetails.shippingFee && (
                      <>
                        <Typography fontSize="12px" fontWeight="inherit" style={{textDecoration: 'line-through'}}>
                          {formatPriceWithCurrency(pricesDetails.originalShippingFee || defaultShippingFee, priceUnit)}
                        </Typography>
                        &nbsp;&nbsp;
                      </>
                    ))}
                  <Typography fontSize="12px" fontWeight="inherit">
                    {pricesDetails.shippingFee === 0 && basket?.basketItems.length ? (
                      <Typography fontSize="12px" color={'primary'}>
                        {t('label.free')}
                      </Typography>
                    ) : (
                      formatPriceWithCurrency(pricesDetails.shippingFee, priceUnit)
                    )}
                  </Typography>
                </Box>
              </DeliveryFeeList>
            ))}
          </ListWrapper>
        </>
      </Collapse>
    )
  });

  if (basket?.voucherId && voucherAmount && !loyaltyDetails?.code && !hasNoSelectedItems) {
    items.push({
      hasExtraOption: !!voucherAmount,
      label: t('label.voucher'),
      labelType: 'voucher',
      priceHighlighted: true,
      price: formatPriceWithCurrency(-voucherAmount, basket.priceUnit),
      children: (
        <Chip
          label={basket.voucherId?.toUpperCase()}
          color="secondary"
          variant="outlined"
          // onDelete={() => removeVoucherCode(basket.voucherId || '', true)}
        />
      )
    });
  }

  if (showLoyaltyOptions && loyaltyDetails?.value > 0 && !hasNoSelectedItems) {
    items.push({
      hasExtraOption: false,
      label: t('label.discount'),
      labelType: 'discount',
      priceHighlighted: true,
      price: formatPriceWithCurrency(loyaltyDetails?.value, basket.priceUnit),
      children: (
        <StyledPointsChip
          icon={<TagSmIcon />}
          label={t('label.makroProPoint')}
          color="secondary"
          variant="outlined"
          deleteIcon={<Close />}
        />
      )
    });
  }

  if (showPointsEarned && (promotions?.estimatedPointsReceive ?? 0) > 0 && !hasNoSelectedItems) {
    items.push({
      hasExtraOption: true,
      label: t('label.estimatedPointsToEarn'),
      labelType: 'estimatedPointsToEarn',
      options: {
        label: t('label.fixedFee'),
        Icon: (
          <Box onClick={togglePointsBreakdown} display="flex" alignItems="center">
            {isPointsBreakdownOpen ? <ChevronUp /> : <ChevronDown />}
          </Box>
        )
      },
      price: promotions.isLoading ? (
        <Skeleton animation="wave" variant="rounded" width={32} height={24} />
      ) : (
        <Box alignItems="center" display="flex" gap="4px">
          <Points />
          {promotions?.estimatedPointsReceive ?? 0}
        </Box>
      ),
      collapse: (
        <Collapse in={isPointsBreakdownOpen} timeout="auto" unmountOnExit>
          <Stack pl={2} gap={1} pb={2}>
            {estimatedPointsDetails?.map((estimatedPointsDetail) => (
              <Box display="flex" justifyContent="space-between">
                <Typography>
                  {isThai ? estimatedPointsDetail.campaignNameTh : estimatedPointsDetail.campaignName}
                </Typography>{' '}
                <Typography>+{estimatedPointsDetail.points}</Typography>
              </Box>
            ))}
            {!!closestNonNegativeExpiringPoint && (
              <Box display="flex" justifyContent="space-between">
                <Typography color="#F22643">
                  *
                  {t('proPointPromotion.expiringPointsLabel', {
                    expiryDate: formatExpiryDate(closestNonNegativeExpiringPoint.expiryDate)
                  })}
                </Typography>
                <Typography color="#F22643">{closestNonNegativeExpiringPoint.points.toLocaleString()}</Typography>
              </Box>
            )}
          </Stack>
        </Collapse>
      )
    });
  }

  const basketTotal = hasNoSelectedItems ? 0 : basket?.prices?.totalFinalPriceToPay || 0;

  const PriceSummaryTitle = (props?: {fontSize?: string; fontWeight?: string}) => (
    <Typography component={'h4'} variant="h4" fontSize={props?.fontSize} fontWeight={props?.fontWeight}>
      {t('flexiBasket.priceSummaryTitle')}
    </Typography>
  );

  const ClosePriceSummaryButton = (props?: {width?: string; strokeWidth?: string; height?: string}) => (
    <IconButton size="small" color="inherit" onClick={() => setShowBasketSummaryModal(false)} aria-label="close">
      <CloseIcon height={props?.height} width={props?.width} strokeWidth={props?.strokeWidth} />
    </IconButton>
  );

  return (
    <BasketSummaryDialog open={showBasketSummaryModal} anchor="bottom" onClose={() => setShowBasketSummaryModal(false)}>
      <BasketSummaryTitle isReactNativeView={isMobileScreen}>
        {isMobileScreen ? (
          <SummaryModalAppHeader
            headerTitle={<PriceSummaryTitle fontSize={'20px'} fontWeight={'700'} />}
            trailingElement={<ClosePriceSummaryButton width={'24px'} height={'24px'} strokeWidth={'3'} />}
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <PriceSummaryTitle fontSize={'20px'} fontWeight={'600'} />
            <ClosePriceSummaryButton strokeWidth={'3'} />
          </Box>
        )}
      </BasketSummaryTitle>

      <BasketSummaryContent>
        <StyledBasketSummaryWrapper isReactNativeView={isMobileScreen} data-test-basket-calculations="">
          <StyledUlContainer isReactNativeView={isMobileScreen}>
            {items.map((item, key) => {
              return (
                <StyledLI data-test-label={item.labelType} key={key}>
                  <StyledListContainer
                    isReactNativeView={isMobileScreen}
                    disableBorder={isWeightedFreshProduct && item?.labelType === 'total' ? true : false}
                  >
                    <StyledListWrapper alignItems={'center'}>
                      <Box display="flex" flexDirection="column">
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" component="p">
                            {item?.label}
                          </Typography>
                          {item?.hasExtraOption && item.options?.Icon ? (
                            <StyledOptions alignItems={'center'}>
                              <StyledInfoButton>{item.options?.Icon}</StyledInfoButton>
                            </StyledOptions>
                          ) : null}
                        </Box>
                        {item?.children || null}
                      </Box>
                    </StyledListWrapper>
                    <StyledListWrapper textAlign={'right'}>
                      {item?.price !== '0.00 ฿' && (
                        <Typography
                          color={item.priceHighlighted ? 'primary' : 'secondary'}
                          variant="body2"
                          component="p"
                        >
                          <b>{item?.price}</b>
                        </Typography>
                      )}
                    </StyledListWrapper>
                  </StyledListContainer>
                  {isWeightedFreshProduct && item.labelType === 'total' && (
                    <StyledBorder>
                      <StyledBox>
                        <StyledRow>
                          <InfoIcon />
                          <Typography style={{textAlign: 'left'}}>{t('weightedFresh.additionalMsg')}</Typography>
                        </StyledRow>
                      </StyledBox>
                    </StyledBorder>
                  )}
                  {item?.collapse || null}
                </StyledLI>
              );
            })}
            <Box width="100%" display="flex" flexDirection="column" gap={isMobileScreen ? '12px' : '8px'}>
              <Box display="flex" justifyContent="space-between">
                <Typography component="p" variant="h6">
                  <b>{t('label.total')}</b>
                </Typography>
                <Typography component="p" variant="h6" fontWeight={isMobileScreen ? '700' : '600'}>
                  {formatPriceWithCurrency(basketTotal, basket?.priceUnit || 'THB')}
                </Typography>
              </Box>
              <Box
                sx={{backgroundColor: '#EBF9FF', color: '#1274E6', padding: '8px 12px 8px 12px', borderRadius: '8px'}}
              >
                <Typography textAlign="center" variant="body2" component="p">
                  {t('flexiBasket.priceSummaryFooter')}
                </Typography>
              </Box>
              {isWeightedFreshItemSelected && (
                <Box
                  sx={{backgroundColor: '#EBF9FF', color: '#1274E6', padding: '8px 12px 8px 12px', borderRadius: '8px'}}
                >
                  <Typography textAlign="center" variant="body2" component="p">
                    {t('weightedFresh.additionalMsg')}
                  </Typography>
                </Box>
              )}
            </Box>
          </StyledUlContainer>
        </StyledBasketSummaryWrapper>
      </BasketSummaryContent>
      <DialogFooter>
        <Footer
          onCheckoutClick={onCheckoutClick}
          isCheckoutDisabled={isCheckoutDisabled}
          loading={loading}
          onClickSelectAllItems={onClickSelectAllItems}
          setShowBasketSummaryModal={setShowBasketSummaryModal}
          showBasketSummaryModal={showBasketSummaryModal}
        />
      </DialogFooter>
    </BasketSummaryDialog>
  );
};

export default BasketSummaryModal;
